import CrudService from "services/cruds-service";

/*
 * Which bank accounts a draft invoice lists (finance's rule, mirrored from
 * the EDM e-invoice in backend/src/services/edm/ublBuilder.js):
 *   - customer abroad  -> only the Garanti account in the invoice's selling
 *                         currency (all Garanti accounts if it has none in
 *                         that currency), with its SWIFT code;
 *   - customer in Türkiye -> each page's existing list, unchanged.
 * Rows use the PDF tables' shape: [bank, currency, iban, branch, swift].
 */
export const GARANTI_ACCOUNTS = [
    ["T.GARANTI BANKASI", "USD", "TR20 0006 2001 1730 0009 0929 99", "MERSIN SANAYI", "TGBATRISXXX"],
    ["T.GARANTI BANKASI", "EUR", "TR90 0006 2001 1730 0009 0930 00", "MERSIN SANAYI", "TGBATRISXXX"],
    ["T.GARANTI BANKASI", "GBP", "TR20 0006 2001 1730 0009 0915 44", "MERSIN SANAYI", "TGBATRISXXX"],
    ["T.GARANTI BANKASI", "TRY", "TR94 0006 2001 1730 0006 2962 24", "MERSIN SANAYI", ""],
];

const normalizeCurrency = (value) => {
    const code = String(value || "").trim().toUpperCase();
    return code === "EURO" ? "EUR" : code;
};

// customerCountry is free text ("Ireland", "Türkiye", "TR"...); resolve it
// to an ISO code via the browser's English/Turkish region names.
const countryCodeByName = (() => {
    const normalize = (s) =>
        String(s).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/ı/g, "i").trim();
    let map = null;

    const build = () => {
        map = new Map([["turkey", "TR"], ["usa", "US"], ["uae", "AE"], ["czech republic", "CZ"]]);
        const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        const names = ["en", "tr"].map((locale) => new Intl.DisplayNames([locale], { type: "region", fallback: "none" }));
        for (const a of letters) {
            for (const b of letters) {
                // Skip deprecated/alias codes (UK -> GB, DD -> DE, ...).
                if (Intl.getCanonicalLocales(`und-${a}${b}`)[0] !== `und-${a}${b}`) continue;
                for (const dn of names) {
                    const name = dn.of(a + b);
                    if (name && !map.has(normalize(name))) map.set(normalize(name), a + b);
                }
            }
        }
    };

    return (value) => {
        const raw = String(value || "").trim();
        if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
        if (!raw) return "";
        if (!map) build();
        return map.get(normalize(raw)) || "";
    };
})();

export const isForeignCountry = (country) => {
    const code = countryCodeByName(country);
    return Boolean(code) && code !== "TR";
};

export const bankRowsForInvoice = (domesticRows, { currency, foreign }) => {
    if (!foreign) return domesticRows;

    const sameCurrency = GARANTI_ACCOUNTS.filter((row) => row[1] === normalizeCurrency(currency));
    return sameCurrency.length ? sameCurrency : GARANTI_ACCOUNTS;
};

// Invoices/jobs don't store the customer's country, so the PDF builders look
// it up from the customer card. Any failure counts as domestic, i.e. the
// page keeps its existing bank list.
export const resolveCustomerCountry = async (...sources) => {
    const direct = sources.map((source) => source?.customerCountry).find(Boolean);
    if (direct) return direct;

    const customerId = sources
        .map((source) => source?.customerId?._id || source?.customerId || source?.customer?._id)
        .find((id) => typeof id === "string" && /^[0-9a-f]{24}$/i.test(id));
    if (!customerId) return "";

    try {
        const response = await CrudService.getCustomerById(customerId);
        const data = response?.data?.attributes || response?.attributes || response?.data || response;
        return data?.customerCountry || "";
    } catch {
        return "";
    }
};
